import React from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"

import { stockForType, isKnownStock, canBreakFrom } from "../../utils/stockTypes"
import { FRIDGES, FRIDGE_ORDER } from "./fridges"

/**
 * Which fridge the broken products came from.
 *
 * A fridge with nothing in it cannot be picked — neither one that was emptied nor one that was
 * never declared. The prototype only greys those out; the ticket also asks for a reason to be
 * written under them, so the counter manager is not left guessing why a tile refuses the click.
 *
 * Values come from the prototype source rather than a screenshot.
 */

/** Prototype tones, per tile state. Not in COLORS: that palette does not carry these values. */
const TONES = {
	idle:     { icon: "#5F6368", label: "#3C4043", stock: "#80868B" },
	active:   { icon: "#1565C0", label: "#1565C0", stock: "#1565C0" },
	disabled: { icon: "#BDBDBD", label: "#B0B0AC", stock: "#B0B0AC" }
}

const sx = {
	section: {
		paddingTop: "28px",
		width: "100%",
		display: "flex",
		flexDirection: "column",
		alignItems: "center"
	},
	heading: {
		fontSize: 16,
		color: "#5F6368",
		marginBottom: "14px"
	},
	tiles: {
		display: "flex",
		gap: "16px",
		justifyContent: "center",
		flexWrap: "wrap"
	},
	column: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		maxWidth: 180
	},
	tile: {
		flex: "0 0 auto",
		width: 156,
		height: 94,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: "6px",
		border: "1px solid #E0E0E0",
		background: "#FFFFFF",
		borderRadius: "5px",
		cursor: "pointer",
		userSelect: "none"
	},
	tileSelected: {
		border: "1px solid #2196F3",
		boxShadow: "inset 0 0 0 1px #2196F3",
		background: "#E8F3FE"
	},
	tileDisabled: {
		background: "#F4F4F2",
		border: "1px solid #EDEDEB",
		boxShadow: "none",
		cursor: "not-allowed"
	},
	label: { fontSize: 15 },
	stock: { fontSize: 13 },
	empty: {
		fontSize: 13,
		color: "#B0B0AC",
		marginTop: "6px",
		textAlign: "center"
	}
}

const SelectFlawOrigin = ({ productStock, selected, onChange }) => (
	<Box sx={sx.section}>
		<Typography sx={sx.heading}>Origine du stock</Typography>

		<Box sx={sx.tiles}>
			{FRIDGE_ORDER.map(type => {
				const fridge = FRIDGES[type]
				const available = canBreakFrom(productStock, type)
				const active = selected === type
				const stock = stockForType(productStock, type)

				const state = !available ? "disabled" : active ? "active" : "idle"

				return (
					<Box key={type} sx={sx.column}>
						<Box
							component="button"
							type="button"
							aria-pressed={active}
							aria-label={fridge.shortLabel}
							disabled={!available}
							onClick={() => onChange(type)}
							sx={{
								...sx.tile,
								...(active && available ? sx.tileSelected : {}),
								...(available ? {} : sx.tileDisabled)
							}}
						>
							<fridge.Icon aria-hidden sx={{ fontSize: 30, color: TONES[state].icon }} />
							<Typography sx={{ ...sx.label, color: TONES[state].label }}>{fridge.shortLabel}</Typography>
							<Typography sx={{ ...sx.stock, color: TONES[state].stock }}>
								Stock {isKnownStock(stock) ? stock : 0}
							</Typography>
						</Box>

						{!available && (
							<Typography sx={sx.empty}>Le stock est vide sur ce frigo</Typography>
						)}
					</Box>
				)
			})}
		</Box>
	</Box>
)

export default SelectFlawOrigin
